import type { TranslationKeys } from "@/i18n/types";
import { MIN_AGENCY_POST_DELAY_HOURS } from "@/utils/agencyPostDelay";
import type { FacilityEmrIntegrationInput } from "@/utils/models";
import { EmrType } from "@/utils/models";
import { TIMEZONES } from "../utils";
import { getFacilityChoiceReason } from "./facilityChoice";
import { EMR_FIELD_LABELS, EMR_UUID_FIELD, FACILITY_OID_REGEX, MAX_PCC_FACILITY_ID, UUID_REGEX } from "./generalMetadataConsts";
import type { GeneralFormData } from "./types";

/** Returns save-blocking reasons for invalid EMR fields:
 *  - any row missing a required field (facilityId always; orgId for types that define one)
 *  - the UUID field (which one differs per vendor — see EMR_UUID_FIELD) non-empty and malformed
 *  - PCC Facility ID that exceeds the PostgreSQL int32 max (2,147,483,647)
 */
export function getEmrIntegrationsReasons(integrations: readonly FacilityEmrIntegrationInput[] | undefined): TranslationKeys[] {
	if (!integrations?.length) return [];
	const reasons: TranslationKeys[] = [];

	// Check required fields per type — facilityId is always required; orgId is required when the
	// type defines one (PCC Org UUID, Yardi Tenant ID, Matrix 360 Facility OID).
	const hasMissingField = integrations.some((e) => {
		const labels = EMR_FIELD_LABELS[e.type];
		const missingFacility = !e.facilityId?.trim();
		const missingOrg = labels.orgId !== undefined && !e.orgId?.trim();
		return missingFacility || missingOrg;
	});
	if (hasMissingField) {
		reasons.push("department.config.emr.missingPrimaryId");
	}

	// UUID format check on whichever field holds a UUID for the vendor (PCC orgId / Yardi facilityId).
	if (
		integrations.some((e) => {
			const field = EMR_UUID_FIELD[e.type];
			return field && e[field] && !UUID_REGEX.test(e[field]);
		})
	) {
		reasons.push("department.config.emr.invalidUuid");
	}
	const matrix360 = integrations.find((e) => e.type === EmrType.Matrix360);
	if (matrix360?.orgId && !FACILITY_OID_REGEX.test(matrix360.orgId)) {
		reasons.push("department.config.emr.invalidFacilityOid");
	}
	const pcc = integrations.find((e) => e.type === EmrType.PCC);
	if (pcc?.facilityId && Number(pcc.facilityId) > MAX_PCC_FACILITY_ID) {
		reasons.push("department.config.emr.pccFacilityIdOverflow");
	}
	return reasons;
}

const KNOWN_TIMEZONES = new Set<string>(TIMEZONES);

export function getMissingGeneralFieldReasons(isNewDept: boolean, formData: GeneralFormData): TranslationKeys[] {
	const reasons: TranslationKeys[] = [];

	// Min > max HPPD blocks save; applies to existing depts too, so it precedes the new-dept-only early return.
	const minHppd = Number(formData.minValidHPPDPercent);
	const maxHppd = Number(formData.maxValidHPPDPercent);
	if (Number.isFinite(minHppd) && Number.isFinite(maxHppd) && minHppd > maxHppd) {
		reasons.push("department.config.general.validHPPD.minMax");
	}

	if (
		formData.nurseInactivityAlertDays != null &&
		formData.nurseInactiveAfterDays != null &&
		formData.nurseInactivityAlertDays >= formData.nurseInactiveAfterDays
	) {
		reasons.push("settings.nurse.inactive.after.days.constraint.error");
	}

	// Both horizons are positive-integer weeks (backend enforces > 0); block save on 0/blank/fractional.
	if (!Number.isInteger(formData.autoCreateScheduleWeeksAhead) || formData.autoCreateScheduleWeeksAhead < 1) {
		reasons.push("department.config.general.autoCreateScheduleWeeksAhead.invalid");
	}
	if (!Number.isInteger(formData.departmentDataHistoryWeeks) || formData.departmentDataHistoryWeeks < 1) {
		reasons.push("department.config.general.dataHistoryWeeks.invalid");
	}

	// Clearing the field reads back as 0 (`Number("")`); block save rather than let the backend
	// reject the whole department config over one field.
	if (!Number.isInteger(formData.agencyPostDelayDefaultHours) || formData.agencyPostDelayDefaultHours < MIN_AGENCY_POST_DELAY_HOURS) {
		reasons.push("agency.config.postDelay.invalid");
	}

	if (!isNewDept) return reasons;

	const tz = formData.timezone?.trim();
	if (!tz) {
		reasons.push("department.config.general.timezone.required");
	} else if (!KNOWN_TIMEZONES.has(tz)) {
		reasons.push("department.config.general.timezone.invalid");
	}
	if (!formData.departmentType) reasons.push("department.config.general.departmentType.required");
	const facilityReason = getFacilityChoiceReason(formData.facility);
	if (facilityReason) reasons.push(facilityReason);

	return reasons;
}
